import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';

const UserManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const dept = useDepartment();
  const permissions = getPagePermissions('/admin/users');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('userManager', {
    currentPage: 1,
    itemsPerPage: 30,
    search: '',
    startDate: '',
    endDate: '',
  }, ['/admin/users']);
  const { currentPage, itemsPerPage, search, startDate, endDate } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearch       = (v) => setFilters({ search: v });
  const setStartDate    = (v) => setFilters({ startDate: v });
  const setEndDate      = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle('Users');
  }, [setPageTitle]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search, startDate, endDate, itemsPerPage]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await userService.getAll(params);
      setUsers(response.data.data);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleDateRangeFilter = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleView = (user) => {
    navigate(`/admin/users/view/${user.id}`);
  };

  const handleEdit = (user) => {
    navigate(`/admin/users/edit/${user.id}`);
  };

  const handleAddUser = () => {
    navigate('/admin/users/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this user?');
    if (confirmed) {
      try {
        await userService.delete(id);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete user');
        toast.error(err.response?.data?.error || 'Failed to delete user');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (user, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [user.id]: true }));
    
    try {
      await userService.updateStatus(user.id, newStatus);
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [user.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          filters={{
            search: {
              placeholder: `Search by User ID, Name, Email, ${dept.region}, or Branch...`,
              value: search,
              onChange: (e) => handleSearch(e.target.value)
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: (e) => handleDateRangeFilter(e.target.value, endDate),
            //   onEndDateChange: (e) => handleDateRangeFilter(startDate, e.target.value)
            // }
          }}
          addButton={permissions.can_add ? {
            label: 'Add User',
            onClick: handleAddUser,
            variant: 'primary'
          } : null}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            { 
              header: 'S.No', 
              accessor: 'serial',
              width: '80px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            { 
              header: 'User ID', 
              accessor: 'user_id',
              width: '120px',
              render: (value) => (
                <span className="text-sm text-gray-900 font-medium" style={{ fontFamily: 'monospace' }}>
                  {value || '-'}
                </span>
              )
            },
            { 
              header: 'Name', 
              accessor: 'name',
              width: '180px',
              render: (value) => (
                <span className="text-sm text-gray-900 font-medium" title={value}>
                  {value && value.length > 25 ? `${value.substring(0, 25)}...` : value}
                </span>
              )
            },
            { 
              header: 'Email', 
              accessor: 'email',
              width: '220px',
              render: (value) => (
                <span className="text-sm text-gray-700" title={value}>
                  {value && value.length > 30 ? `${value.substring(0, 30)}...` : value}
                </span>
              )
            },
            { 
              header: 'Role', 
              accessor: 'role_name',
              width: '100px',
              render: (value) => (
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  value === 'Admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {value || 'Agent'}
                </span>
              )
            },
            { 
              header: dept.regions, 
              accessor: 'region_names',
              width: '180px',
              render: (value) => value ? (
                <span className="text-sm text-gray-700" title={value}>
                  {value.length > 25 ? `${value.substring(0, 25)}...` : value}
                </span>
              ) : (
                <span className="text-sm text-gray-400">No regions</span>
              )
            },
            { 
              header: 'Branches', 
              accessor: 'branch_names',
              width: '180px',
              render: (value) => value ? (
                <span className="text-sm text-gray-700" title={value}>
                  {value.length > 25 ? `${value.substring(0, 25)}...` : value}
                </span>
              ) : (
                <span className="text-sm text-gray-400">No branches</span>
              )
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                  disabled={!permissions.can_edit}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '100px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            },
          ]}
          data={permissions.can_list ? users : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No users found'}
          loading={loading}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        
      </Card>
    </div>
  );
};

export default UserManager;
